import { Component, effect, forwardRef, inject, input, OnDestroy, OnInit } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import {
    ControlValueAccessor,
    FormsModule,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { isNonEmptyString } from '@trade-platform/ui-utils';
import { isArray } from 'lodash-es';
import {
    AixDataTestingDirective,
    AixDropdownComponent,
    AixInputComponent,
    AixNextPendingFieldDirective,
    AixZipComponent
} from '@trade-platform/ui-components';
import { countries, OneOfModel, states } from '../../store/start-order/constants';

@Component({
    selector: 'aix-address',
    templateUrl: './address.html',
    styleUrls: ['./address.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AixAddressGroupComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: AixAddressGroupComponent
        }
    ],
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        AixDropdownComponent,
        AixInputComponent,
        AixZipComponent,
        AixNextPendingFieldDirective,
        AixDataTestingDirective
    ]
})
export class AixAddressGroupComponent implements ControlValueAccessor, OnInit, OnDestroy {
    private _fb = inject(UntypedFormBuilder);

    title = input<string>();
    includePOBox = input<boolean>();
    isRequired = input<boolean>(true);
    isDisabled = input<boolean>(false);
    omitCountry = input<boolean>(false);
    prefix = input<string>('');

    isDestroying = false;
    states: OneOfModel[] = states;
    countries: OneOfModel[] = countries;
    form: UntypedFormGroup;

    subscriptions: Subscription[] = [];

    private _onChange: (value: any | null | undefined) => void;
    private _destroy$: Subject<void> = new Subject<void>();

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        this._createFormGroup();
        this._setupObservables();

        // Reads isRequired() as well as omitCountry(): it used to re-apply
        // Validators.required to `country` unconditionally, which ran AFTER ngOnInit had
        // cleared it and so overrode a consumer that asked for an optional address.
        effect(() => {
            if (this.omitCountry() || !this.isRequired()) {
                this.form.get('country')?.clearValidators();
            } else {
                this.form.get('country')?.setValidators([Validators.required]);
            }
            this.form.get('country')?.updateValueAndValidity();
            this.form.updateValueAndValidity();
        });
    }

    ngOnInit() {
        this.isDestroying = false;
        if (!this.isRequired()) {
            this.clearRequired('country');
            this.clearRequired('street');
            this.clearRequired('city');
            this.clearRequired('state');
            this.clearRequired('zip');
            this.form.updateValueAndValidity();
        }
        this.patchForm();
    }

    /**
     * clearValidators() alone is not enough, and the difference is a form that can never be
     * submitted (trade-platform/roadmap#321).
     *
     * _createFormGroup builds all five controls with Validators.required and a null value, so
     * the FormControl constructor caches status INVALID on each. clearValidators() only nulls
     * the validator function -- Angular documents that it does NOT recompute -- and the group
     * call below reads its children's CACHED status, so the four stay INVALID.
     *
     * validate() then reports the whole address invalid to the outer control, and the later
     * per-control setup that would have cleaned it up runs with emitEvent: false, so the outer
     * control never re-validates and the error is frozen in. Only a consumer that happens to
     * write all five keys (investorToAddressControl does; `{ country: 'US' }` does not) clears
     * it by accident, which is exactly why editing a contact worked and adding one did not.
     */
    private clearRequired(path: string) {
        const control = this.form.get(path);
        control?.clearValidators();
        control?.updateValueAndValidity({ emitEvent: false });
    }

    patchForm() {
        const data = {};
        this.form.patchValue(data);
    }

    ngOnDestroy() {
        this.isDestroying = true;
        this.form.updateValueAndValidity();
        this.subscriptions.forEach(s => s.unsubscribe());

        if (this._destroy$ && !this._destroy$.closed) {
            this._destroy$.next();
            this._destroy$.complete();
        }
    }

    validate(ctrl: UntypedFormControl) {
        return this.form.valid || this.isDestroying ? null : { invalid: true };
    }

    writeValue(value: any | null | undefined): void {
        if (!value) {
            this.clearField('country');
            this.clearField('street');
            this.clearField('city');
            this.clearField('state');
            this.clearField('zip');
            return;
        }

        if (value.country && isNonEmptyString(value.country)) {
            let countryOption;
            if (value.country.length == 2) {
                countryOption = countries.find(c => c.id === value.country);
            } else {
                countryOption = countries.find(c => c.name === value.country);
            }
            if (countryOption) {
                value.country = [countryOption];
            }
        }
        if (value.state && isNonEmptyString(value.state) && value.state.length == 2) {
            const stateOption = states.find(s => s.id === value.state);

            if (stateOption) {
                if (value.country && value.country[0].id !== 'US') {
                    value.state = stateOption.name;
                } else {
                    value.state = [stateOption];
                }
            }
        }
        this.form.patchValue(value);
        this.form.updateValueAndValidity();
    }

    registerOnChange(fn: (value: any | null | undefined) => void): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        // TODO: implement this method
        // throw new Error('registerOnTouched not implemented');
    }

    clearField(path: string) {
        this.form.get(path)?.setValue(null);
    }

    onCountryChange(e: any) {
        setTimeout(() => {
            if (e && e.length === 1 && e[0].id !== 'US' && isArray(this.form?.value?.state)) {
                this.clearField('state');
            } else if (
                e &&
                (e.length === 0 || e[0]?.id === 'US') &&
                !isArray(this.form?.value?.state)
            ) {
                this.clearField('state');
            }
            this.form.updateValueAndValidity();
        }, 100);
    }

    private _createFormGroup() {
        const formControls: any = {
            street: this._fb.control(null, [Validators.required]),
            city: this._fb.control(null, [Validators.required]),
            state: this._fb.control(null, [Validators.required]),
            zip: this._fb.control(null, [Validators.required])
        };
        if (!this.omitCountry()) {
            formControls.country = this._fb.control(null, [Validators.required]);
        }
        this.form = this._fb.group(formControls);
    }

    private _setupObservables() {
        this.form.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(value => {
            if (this._onChange) {
                this._onChange(value);
            }
        });
    }
}
